import { CSSProperties } from 'react';

// material-ui
import { alpha, styled } from '@mui/material/styles';
import { Box, Theme, useMediaQuery, useTheme } from '@mui/material';

// third-party
import SimpleBar, { Props } from 'simplebar-react';
import { MUIStyledCommonProps } from '@mui/system';

// scroll bar wrapper
const SimpleBarStyle = styled(SimpleBar)(({ theme }) => ({
  maxHeight: '100%',
  '& .simplebar-scrollbar': {
    '&:before': {
      backgroundColor: alpha(theme.palette.grey[500], 0.48)
    },
    '&.simplebar-visible:before': {
      opacity: 1
    }
  },
  '& .simplebar-track.simplebar-vertical': {
    width: 10
  },
  '& .simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
    height: 6
  },
  '& .simplebar-mask': {
    zIndex: 'inherit'
  }
}));

// ==============================|| SIMPLE SCROLL BAR  ||============================== //

// `style` is declared explicitly: simplebar-react's own Props type doesn't carry it,
// but callers pass one and both branches below forward it via `...other`.
type SimpleBarScrollProps = MUIStyledCommonProps<Theme> & Props & { style?: CSSProperties };

export default function SimpleBarScroll({ children, sx, ...other }: SimpleBarScrollProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  // Use native scrolling on mobile/touch devices for better performance
  if (isMobile) {
    return (
      <Box 
        sx={{ 
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflowX: 'hidden', 
          overflowY: 'auto', 
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          msOverflowStyle: '-ms-autohiding-scrollbar',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '3px'
          },
          ...sx 
        }} 
        {...other}
      >
        {children}
      </Box>
    );
  }

  // Use SimpleBar on desktop for custom scrollbar styling
  return (
    <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
      <SimpleBarStyle timeout={500} clickOnTrack={false} sx={sx} {...other}>
        {children}
      </SimpleBarStyle>
    </Box>
  );
}
